import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Divider, Drawer, FormControl, InputLabel,
    LinearProgress, MenuItem, Select, Slide, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckIcon from '@mui/icons-material/Check';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import kbaseService from '../services/KbaseService';
import conversationHistoryService from '../services/ConversationHistoryService';

const DOC_PAGE_REF_RE = /\[([^\]]+):([a-f0-9]{20,})\/(\d{1,6})\]/gi;

function MarkdownWithCitations({ children, sx = {} }) {
    const { t } = useTranslation();
    let citationNumber = 0;
    const renderCitations = (value) => {
        if (typeof value !== 'string') return value;
        const parts = [];
        let lastIndex = 0;
        const regex = new RegExp(DOC_PAGE_REF_RE.source, 'gi');
        let match;
        while ((match = regex.exec(value)) !== null) {
            if (match.index > lastIndex) parts.push(value.slice(lastIndex, match.index));
            citationNumber += 1;
            const label = t('kbaseFeedback.citationLabel', 'Source {{n}}: {{source}}, page {{page}}', {
                n: citationNumber, source: match[1], page: match[3],
            });
            parts.push(<Box component="span" key={`${match.index}-${citationNumber}`} title={label} aria-label={label} sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, mx: 0.25, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '9px', fontWeight: 700, lineHeight: 1, verticalAlign: 'text-bottom' }}>{citationNumber}</Box>);
            lastIndex = regex.lastIndex;
        }
        if (lastIndex < value.length) parts.push(value.slice(lastIndex));
        return parts.length ? parts : value;
    };
    const renderChildren = (value) => Array.isArray(value)
        ? value.map((child, index) => typeof child === 'string' ? <React.Fragment key={index}>{renderCitations(child)}</React.Fragment> : child)
        : renderCitations(value);

    return <Box sx={sx}><ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
            a: ({ node, children: linkChildren, ...props }) => <a {...props} target="_blank" rel="noreferrer">{linkChildren}</a>,
            p: ({ node, children: paragraphChildren }) => <p>{renderChildren(paragraphChildren)}</p>,
            li: ({ node, children: listChildren }) => <li>{renderChildren(listChildren)}</li>,
        }}
    >{children}</ReactMarkdown></Box>;
}

function statusLabel(value) {
    return String(value || '').replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function responseConfidence(failureConfidence) {
    const failure = Number(failureConfidence);
    return Number.isFinite(failure) ? Math.max(0, Math.min(100, 100 - failure)) : 0;
}

function scoreLevel(score, t) {
    if (score >= 70) return { label: t('kbaseFeedback.scoreHigh', 'High'), color: '#2e7d32' };
    if (score >= 40) return { label: t('kbaseFeedback.scoreMedium', 'Medium'), color: '#ed9b00' };
    return { label: t('kbaseFeedback.scoreLow', 'Low'), color: '#df6c00' };
}

function KbaseFeedback({ kbaseId, onCountChange }) {
    const { t, i18n } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [selected, setSelected] = useState(null);
    const [editing, setEditing] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [confirmingUnpublish, setConfirmingUnpublish] = useState(false);
    const [folders, setFolders] = useState([]);
    const [summary, setSummary] = useState({ unresolved: 0, needsSource: 0, resolved: 0 });
    const [draft, setDraft] = useState({ title: '', content: '', approvedSource: '', folder: '' });
    const [conversation, setConversation] = useState(null);
    const [conversationLoading, setConversationLoading] = useState(false);
    const [conversationError, setConversationError] = useState('');
    const [showConversationHistory, setShowConversationHistory] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const [feedbackRows, allFeedbackRows] = await Promise.all([
                kbaseService.getKbaseFeedback(kbaseId, { status }),
                status === 'all' ? Promise.resolve(null) : kbaseService.getKbaseFeedback(kbaseId, { status: 'all' }),
            ]);
            const completeRows = allFeedbackRows || feedbackRows;
            setRows(feedbackRows);
            onCountChange?.(completeRows.length);
            setSummary({
                unresolved: completeRows.filter((row) => row.feedbackStatus === 'new').length,
                needsSource: completeRows.filter((row) => row.feedbackStatus === 'needs_source').length,
                resolved: completeRows.filter((row) => row.feedbackStatus === 'resolved').length,
            });
        } catch (e) {
            setError(e?.message || t('kbaseFeedback.errors.load', 'Failed to load feedback'));
        } finally {
            setLoading(false);
        }
    }, [kbaseId, onCountChange, status, t]);

    const statusText = (value) => t(`kbaseFeedback.statuses.${value}`, statusLabel(value));
    const signalText = (value) => t(`kbaseFeedback.signals.${value}`, statusLabel(value));

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        kbaseService.getKbaseDocuments(kbaseId).then((documents) => {
            setFolders(documents.filter((document) => String(document.documentType).toLowerCase() === 'folder'));
        }).catch(() => setFolders([]));
    }, [kbaseId]);

    useEffect(() => {
        if (!selected?.conversationId || !showConversationHistory) {
            setConversation(null);
            setConversationError('');
            return undefined;
        }

        let cancelled = false;
        setConversationLoading(true);
        setConversationError('');
        conversationHistoryService.getConversationDetail(selected.conversationId)
            .then((detail) => { if (!cancelled) setConversation(detail); })
            .catch((e) => {
                if (!cancelled) setConversationError(e?.message || t('kbaseFeedback.conversationLoadError', 'Failed to load conversation'));
            })
            .finally(() => { if (!cancelled) setConversationLoading(false); });

        return () => { cancelled = true; };
    }, [selected?.conversationId, showConversationHistory, t]);

    const openDetail = (row) => {
        setSelected(row);
        setShowConversationHistory(false);
        setEditing(Boolean(row.draftTitle) && !row.publishedDocumentId);
        setPreviewing(false);
        setDraft({
            title: row.draftTitle || row.question || '',
            content: row.draftContent || '',
            approvedSource: row.draftSource || '',
            folder: row.draftFolder || '',
        });
    };

    useEffect(() => {
        if (!selected || selected.feedbackStatus !== 'publishing') return undefined;
        const timer = window.setInterval(async () => {
            try {
                const currentRows = await kbaseService.getKbaseFeedback(kbaseId, { status: 'all' });
                const updated = currentRows.find((row) => row.assessmentId === selected.assessmentId);
                if (updated) {
                    setSelected(updated);
                    setRows(currentRows);
                }
            } catch (_) {
                // Keep the publishing confirmation visible; the next poll can retry.
            }
        }, 3000);
        return () => window.clearInterval(timer);
    }, [kbaseId, selected, status]);

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        return rows.filter((row) => !query || `${row.question || ''} ${row.answer || ''}`.toLowerCase().includes(query));
    }, [rows, search]);

    const updateStatus = async (nextStatus) => {
        if (!selected) return;
        try {
            const updated = await kbaseService.updateKbaseFeedback(kbaseId, selected.assessmentId, {
                status: nextStatus,
                assessmentIds: selected.assessmentIds || [selected.assessmentId],
            });
            setRows((current) => current.map((row) => row.assessmentId === updated.assessmentId ? updated : row));
            setSelected(updated);
        } catch (e) {
            setError(e?.message || t('kbaseFeedback.errors.update', 'Failed to update feedback'));
        }
    };

    const saveDraft = async () => {
        if (!draft.title.trim() || !draft.content.trim() || !draft.approvedSource.trim()) {
            setError(t('kbaseFeedback.errors.required', 'Title, answer, and approved source are required.')); return;
        }
        try {
            setSaving(true); setError('');
            const updated = await kbaseService.saveKbaseFeedbackDraft(kbaseId, selected.assessmentId, {
                ...draft, assessmentIds: selected.assessmentIds || [selected.assessmentId],
            });
            setSelected(updated);
            setRows((current) => current.map((row) => row.assessmentId === updated.assessmentId ? updated : row));
            setPreviewing(true);
        } catch (e) { setError(e?.message || t('kbaseFeedback.errors.save', 'Failed to save draft')); }
        finally { setSaving(false); }
    };

    const publishDraft = async () => {
        try {
            setSaving(true); setError('');
            const updated = await kbaseService.publishKbaseFeedbackDraft(kbaseId, selected.assessmentId, {
                assessmentIds: selected.assessmentIds || [selected.assessmentId], folder: draft.folder || null,
            });
            setSelected(updated);
            setRows((current) => current.map((row) => row.assessmentId === updated.assessmentId ? updated : row));
            setPreviewing(false);
            setEditing(false);
        } catch (e) { setError(e?.message || t('kbaseFeedback.errors.publish', 'Failed to publish draft')); }
        finally { setSaving(false); }
    };

    const unpublishDraft = async () => {
        if (!selected || selected.feedbackStatus === 'publishing') return;
        try {
            setSaving(true); setError('');
            const updated = await kbaseService.unpublishKbaseFeedbackDraft(kbaseId, selected.assessmentId, {
                assessmentIds: selected.assessmentIds || [selected.assessmentId],
            });
            setSelected(updated);
            setRows((current) => current.map((row) => row.assessmentId === updated.assessmentId ? updated : row));
            setDraft({
                title: updated.draftTitle || '', content: updated.draftContent || '',
                approvedSource: updated.draftSource || '', folder: updated.draftFolder || '',
            });
            setEditing(true);
            setPreviewing(false);
            setConfirmingUnpublish(false);
        } catch (e) { setError(e?.message || t('kbaseFeedback.errors.unpublish', 'Failed to unpublish article')); }
        finally { setSaving(false); }
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(260px, 336px))',
                        md: 'repeat(3, minmax(260px, 336px))',
                    },
                    gap: 2.25,
                    justifyContent: 'start',
                    mb: 3,
                }}
            >
                {[
                    { key: 'unresolved', label: t('kbaseFeedback.summary.unresolved', 'Unresolved'), count: summary.unresolved, icon: <HelpOutlineIcon /> },
                    { key: 'needsSource', label: t('kbaseFeedback.summary.needsSource', 'Needs source'), count: summary.needsSource, icon: <CheckBoxOutlineBlankIcon /> },
                    { key: 'resolved', label: t('kbaseFeedback.summary.resolved', 'Resolved'), count: summary.resolved, icon: <CheckIcon /> },
                ].map((item) => (
                    <Box
                        key={item.key}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            width: '100%',
                            height: 88,
                            boxSizing: 'border-box',
                            px: 1.75,
                            py: 1.25,
                            bgcolor: '#fff',
                            border: '1px solid #dfe3e8',
                            borderRadius: '14px',
                            boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
                        }}
                    >
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                flex: '0 0 44px',
                                display: 'grid',
                                placeItems: 'center',
                                color: '#f36b21',
                                bgcolor: '#fff2e9',
                                borderRadius: '12px',
                                '& svg': { fontSize: 25 },
                            }}
                        >
                            {item.icon}
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#687386', fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3 }}>
                                {item.label}
                            </Typography>
                            <Typography sx={{ color: '#182033', fontWeight: 700, fontSize: '1.5rem', lineHeight: 1.05 }}>
                                {item.count}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <TextField size="small" fullWidth label={t('kbaseFeedback.search', 'Search feedback questions')} value={search} onChange={(e) => setSearch(e.target.value)} />
                <FormControl size="small" sx={{ minWidth: 190 }}>
                    <InputLabel>{t('kbaseFeedback.status', 'Status')}</InputLabel>
                    <Select label={t('kbaseFeedback.status', 'Status')} value={status} onChange={(e) => setStatus(e.target.value)}>
                        <MenuItem value="all">{t('kbaseFeedback.statuses.all', 'All statuses')}</MenuItem><MenuItem value="new">{statusText('new')}</MenuItem>
                        <MenuItem value="needs_source">{statusText('needs_source')}</MenuItem><MenuItem value="draft">{statusText('draft')}</MenuItem>
                        <MenuItem value="awaiting_approval">{statusText('awaiting_approval')}</MenuItem><MenuItem value="resolved">{statusText('resolved')}</MenuItem>
                    </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={load}
                    disabled={loading}
                    sx={{ minWidth: 120, whiteSpace: 'nowrap' }}
                >
                    {loading ? t('kbaseFeedback.refreshing', 'Refreshing…') : t('kbaseFeedback.refresh', 'Refresh')}
                </Button>
            </Stack>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box> : (
                <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead><TableRow><TableCell>{t('kbaseFeedback.topic', 'Improvement candidate')}</TableCell><TableCell>{t('kbaseFeedback.status', 'Status')}</TableCell><TableCell>{t('kbaseFeedback.quality', 'Quality')}</TableCell><TableCell>{t('kbaseFeedback.confidence', 'Confidence')}</TableCell><TableCell>{t('kbaseFeedback.updated', 'Last detected')}</TableCell></TableRow></TableHead>
                        <TableBody>
                            {filtered.map((row) => <TableRow hover key={row.assessmentId} onClick={() => openDetail(row)} sx={{ cursor: 'pointer' }}>
                                <TableCell><Typography fontWeight={650}>{row.question || t('kbaseFeedback.untitled', 'Untitled question')}</Typography><Typography variant="caption" color="text.secondary">{row.occurrenceCount > 1 ? `${t('kbaseFeedback.occurrences', '{{count}} occurrences', { count: row.occurrenceCount })} · ` : ''}{(row.signals || []).map(signalText).join(' · ')}</Typography></TableCell>
                                <TableCell><Chip size="small" label={statusText(row.feedbackStatus)} color={row.feedbackStatus === 'resolved' ? 'success' : 'primary'} variant="outlined" /></TableCell>
                                <TableCell>{row.qualityScore}/100</TableCell><TableCell>{responseConfidence(row.failureConfidence)}%</TableCell>
                                <TableCell>{new Date(Number(row.updatedAt) * 1000).toLocaleDateString(i18n.language === 'jp' ? 'ja-JP' : 'en-US')}</TableCell>
                            </TableRow>)}
                            {!filtered.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>{t('kbaseFeedback.empty', 'No actionable feedback found.')}</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <Drawer anchor="right" open={Boolean(selected)} onClose={() => { setSelected(null); setShowConversationHistory(false); }} PaperProps={{ sx: { width: { xs: '100%', md: showConversationHistory ? 1000 : 650 }, p: 3, transition: 'width 225ms ease' } }}>
                {selected && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: showConversationHistory ? 'minmax(0, 1fr) minmax(360px, 0.9fr)' : 'minmax(0, 1fr)' }, gap: 3, height: '100%' }}>
                <Stack spacing={0} sx={{ minWidth: 0, minHeight: 0, height: '100%', overflow: 'hidden' }}>
                <Stack spacing={2} sx={{ minHeight: 0, overflowY: 'auto', pr: 0.75, pb: 2 }}>
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 2 }}><Chip size="small" label={statusText(selected.feedbackStatus)} color="primary" variant="outlined" /><Button size="small" variant="outlined" onClick={() => setShowConversationHistory((value) => !value)}>{showConversationHistory ? t('kbaseFeedback.hideConversation', 'Hide conversation history') : t('kbaseFeedback.showConversation', 'Show conversation history')}</Button></Stack>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 0.75 }}>
                            <Box sx={{ p: 0.875, minHeight: 74, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                                <Typography color="text.secondary" fontWeight={600} sx={{ fontSize: '0.7rem' }}>{t('kbaseFeedback.timesAsked', 'Times asked')}</Typography>
                                <Typography fontWeight={700} sx={{ mt: 0.5, fontSize: '1.3rem', lineHeight: 1.2 }}>{selected.occurrenceCount}</Typography>
                            </Box>
                            {[{
                                label: t('kbaseFeedback.answerQuality', 'Answer quality'),
                                score: Number(selected.qualityScore) || 0,
                                suffix: '%',
                            }, {
                                label: t('kbaseFeedback.aiConfidence', 'AI confidence'),
                                score: responseConfidence(selected.failureConfidence),
                                suffix: '%',
                            }].map((metric) => {
                                const level = scoreLevel(metric.score, t);
                                return <Box key={metric.label} sx={{ p: 0.875, minHeight: 74, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                                    <Typography color="text.secondary" fontWeight={600} sx={{ fontSize: '0.7rem' }}>{metric.label}</Typography>
                                    <Typography fontWeight={700} sx={{ mt: 0.5, fontSize: '1.3rem', lineHeight: 1.2 }}>{metric.score}{metric.suffix}</Typography>
                                    <LinearProgress variant="determinate" value={metric.score} sx={{ mt: 0.7, mb: 0.5, height: 6, borderRadius: 5, bgcolor: 'grey.200', '& .MuiLinearProgress-bar': { bgcolor: level.color, borderRadius: 5 } }} />
                                    <Typography sx={{ color: level.color, fontSize: '0.7rem' }}>{level.label}</Typography>
                                </Box>;
                            })}
                        </Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5, color: 'text.secondary' }}>
                            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                            <Typography sx={{ fontSize: '0.75rem' }}>{t('kbaseFeedback.scoreNote', 'Low scores are review signals, not proof of an incorrect answer.')}</Typography>
                        </Stack>
                    </Box>
                    <Box sx={{ p: 1.25, bgcolor: '#eaf3ff', border: 1, borderColor: '#bfdbfe', borderRadius: 2 }}>
                        <Typography color="primary.main" fontWeight={700} sx={{ fontSize: '11px' }}>{t('kbaseFeedback.userQuestion', 'User question')}</Typography>
                        <Typography sx={{ mt: 0.25, fontSize: '13px', lineHeight: 1.35 }}>{selected.question || t('kbaseFeedback.untitled', 'Untitled question')}</Typography>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: 'grey.300', border: 1, borderColor: 'grey.400', borderRadius: 2 }}>
                        <Typography fontWeight={700} gutterBottom sx={{ fontSize: '13px' }}>{t('kbaseFeedback.botAnswer', 'Bot answer')}</Typography>
                        <MarkdownWithCitations sx={{ fontSize: '12px', lineHeight: 1.5, overflowWrap: 'anywhere', '& p': { my: 0, mb: 1 }, '& p:last-child': { mb: 0 }, '& h1, & h2, & h3': { mt: 2, mb: 1, fontWeight: 700 }, '& h1': { fontSize: '17px' }, '& h2': { fontSize: '15px' }, '& h3': { fontSize: '13px' }, '& ul, & ol': { my: 1, pl: 3 }, '& li': { mb: 0.5 }, '& pre': { overflowX: 'auto', p: 1.5, borderRadius: 1, bgcolor: 'grey.100' }, '& code': { fontFamily: 'monospace' }, '& a': { color: 'primary.main' }, '& table': { display: 'block', maxWidth: '100%', overflowX: 'auto', borderCollapse: 'collapse' }, '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5 } }}>{selected.answer || t('kbaseFeedback.noAnswer', 'No answer text available.')}</MarkdownWithCitations>
                    </Box>
                    <Box>
                        <Typography fontWeight={700} sx={{ mb: 0.5 }}>{t('kbaseFeedback.whyFlagged', 'Why it was flagged')}</Typography>
                        <Alert severity="warning" icon={false} sx={{ py: 0.25, px: 1.25, '& .MuiAlert-message': { py: 0.25, lineHeight: 1.35 } }}>
                            {(selected.signals || []).map(signalText).join(', ')}
                        </Alert>
                    </Box>
                    {selected.suggestion && <Box>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                            <AutoAwesomeIcon sx={{ fontSize: 24, color: 'text.primary' }} />
                            <Typography fontWeight={700}>{t('kbaseFeedback.suggestion', 'AI suggestion improvement')}</Typography>
                        </Stack>
                        <Alert severity="info" icon={false} sx={{ py: 0.5, px: 1.25, '& .MuiAlert-message': { py: 0.25, whiteSpace: 'pre-wrap' } }}>
                            {selected.suggestion}
                        </Alert>
                    </Box>}
                    <Divider />
                    {selected.publishedDocumentId ? <>
                        <Alert severity={selected.feedbackStatus === 'resolved' ? 'success' : 'info'}>
                            {selected.feedbackStatus === 'resolved'
                                ? t('kbaseFeedback.publishedSuccess', 'Article published and indexed successfully.')
                                : t('kbaseFeedback.queuedSuccess', 'Article approved and queued for indexing.')}
                        </Alert>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                            <Typography fontWeight={700}>{selected.draftTitle}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', my: 1.5 }}>{selected.draftContent}</Typography>
                            <Typography variant="caption" color="text.secondary">{t('kbaseFeedback.approvedSource', 'Approved source')}: {selected.draftSource}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">{t('kbaseFeedback.document', 'Document')}: {selected.publishedDocumentId}</Typography>
                        </Box>
                        {selected.feedbackStatus === 'publishing' && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={18} /><Typography variant="body2" color="text.secondary">{t('kbaseFeedback.indexing', 'Indexing in progress…')}</Typography></Stack>}
                    </> : !editing ? <>
                        <Typography variant="body2" color="text.secondary">{t('kbaseFeedback.draftHelp', 'Create a non-searchable draft from this feedback. It becomes searchable only after preview, approval, publishing, and indexing.')}</Typography>
                    </> : previewing ? <>
                        <Typography fontWeight={700}>{t('kbaseFeedback.articlePreview', 'Article preview')}</Typography>
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: 1, borderColor: 'divider' }}>
                            <Typography variant="h6">{draft.title}</Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', my: 2 }}>{draft.content}</Typography>
                            <Typography variant="caption" color="text.secondary">{t('kbaseFeedback.approvedSource', 'Approved source')}: {draft.approvedSource}</Typography>
                        </Box>
                    </> : <>
                        <Typography fontWeight={700}>{t('kbaseFeedback.draftArticle', 'Draft article')}</Typography>
                        <TextField label={t('kbaseFeedback.title', 'Title')} value={draft.title} onChange={(e) => setDraft((value) => ({ ...value, title: e.target.value }))} required />
                        <TextField label={t('kbaseFeedback.answer', 'Answer')} value={draft.content} onChange={(e) => setDraft((value) => ({ ...value, content: e.target.value }))} multiline minRows={7} required />
                        <TextField label={t('kbaseFeedback.approvedSource', 'Approved source')} value={draft.approvedSource} onChange={(e) => setDraft((value) => ({ ...value, approvedSource: e.target.value }))} helperText={t('kbaseFeedback.sourceHelp', 'URL, policy, document, or other verified source')} required />
                        <FormControl><InputLabel>{t('kbaseFeedback.documentsFolder', 'Documents folder')}</InputLabel><Select label={t('kbaseFeedback.documentsFolder', 'Documents folder')} value={draft.folder} onChange={(e) => setDraft((value) => ({ ...value, folder: e.target.value }))}>
                            <MenuItem value="">{t('kbaseFeedback.root', 'Root')}</MenuItem>{folders.map((folder) => <MenuItem key={folder.documentId} value={folder.folderName}>{folder.folderName}</MenuItem>)}
                        </Select></FormControl>
                    </>}
                </Stack>
                <Box sx={{ flexShrink: 0, pt: 1.5, mt: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                    {selected.publishedDocumentId ? (
                        <Button variant="outlined" color="error" onClick={() => setConfirmingUnpublish(true)} disabled={saving || selected.feedbackStatus === 'publishing'}>
                            {t('kbaseFeedback.unpublish', 'Unpublish')}
                        </Button>
                    ) : !editing ? (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button variant="contained" onClick={() => setEditing(true)}>{t('kbaseFeedback.createDraft', 'Create draft article')}</Button>
                            <Button variant="outlined" onClick={() => updateStatus('needs_source')}>{statusText('needs_source')}</Button>
                            <Button variant="text" color="inherit" onClick={() => updateStatus('dismissed')}>{t('kbaseFeedback.dismiss', 'Dismiss')}</Button>
                        </Stack>
                    ) : previewing ? (
                        <Stack direction="row" spacing={1}>
                            <Button onClick={() => setPreviewing(false)}>{t('kbaseFeedback.backToEdit', 'Back to edit')}</Button>
                            <Button variant="contained" onClick={publishDraft} disabled={saving || selected.feedbackStatus === 'publishing'}>{saving ? t('kbaseFeedback.publishing', 'Publishing…') : t('kbaseFeedback.approvePublish', 'Approve & publish')}</Button>
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1}>
                            <Button variant="contained" onClick={saveDraft} disabled={saving}>{saving ? t('kbaseFeedback.saving', 'Saving…') : t('kbaseFeedback.savePreview', 'Save draft & preview')}</Button>
                            <Button color="inherit" onClick={() => setEditing(false)}>{t('kbaseFeedback.cancel', 'Cancel')}</Button>
                        </Stack>
                    )}
                </Box>
                </Stack>
                <Slide direction="left" in={showConversationHistory} mountOnEnter unmountOnExit>
                <Box sx={{ borderLeft: { md: 1 }, borderTop: { xs: 1, md: 0 }, borderColor: 'divider', p: 2, minWidth: 0, bgcolor: 'grey.300', borderRadius: 2 }}>
                    <Typography fontWeight={700} variant="h6" gutterBottom>{t('kbaseFeedback.conversationHistory', 'Conversation History')}</Typography>
                    {conversationLoading ? <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={26} /></Box>
                        : conversationError ? <Alert severity="error">{conversationError}</Alert>
                            : <Stack spacing={1.25} sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 110px)', pr: 1 }}>
                                {(conversation?.messages || []).filter((message) => String(message.messageType || '').toLowerCase() !== 'system').map((message, index) => {
                                    const isUser = String(message.messageType || '').toLowerCase() === 'user';
                                    return <Box key={message.conversationHistoryId || index} sx={{ alignSelf: isUser ? 'flex-end' : 'flex-start', maxWidth: '88%', px: 1.5, py: 1, borderRadius: 2, bgcolor: isUser ? 'primary.main' : 'common.white', color: isUser ? 'primary.contrastText' : 'text.primary' }}>
                                        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mb: 0.25, opacity: 0.75 }}>
                                            <Typography variant="caption">{isUser ? t('kbaseFeedback.user', 'User') : t('kbaseFeedback.assistant', 'Assistant')}</Typography>
                                            <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                                {message.createdAt ? new Date(Number(message.createdAt) * 1000).toLocaleString(i18n.language === 'jp' ? 'ja-JP' : 'en-US', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                                            </Typography>
                                        </Stack>
                                        <MarkdownWithCitations sx={{ fontSize: '0.875rem', overflowWrap: 'anywhere', '& p': { my: 0 }, '& p + p': { mt: 1 }, '& ul, & ol': { my: 0.75, pl: 2.5 }, '& pre': { overflowX: 'auto', p: 1, borderRadius: 1, bgcolor: 'rgba(0, 0, 0, 0.06)' }, '& code': { fontFamily: 'monospace' }, '& a': { color: 'inherit', textDecoration: 'underline' } }}>{message.messageContent || ''}</MarkdownWithCitations>
                                    </Box>;
                                })}
                                {!conversation?.messages?.length && <Typography color="text.secondary" variant="body2">{t('kbaseFeedback.noConversationMessages', 'No conversation messages available.')}</Typography>}
                            </Stack>}
                </Box>
                </Slide>
                </Box>}
            </Drawer>
            <Dialog
                open={confirmingUnpublish}
                onClose={() => { if (!saving) setConfirmingUnpublish(false); }}
                aria-labelledby="unpublish-dialog-title"
            >
                <DialogTitle id="unpublish-dialog-title">{t('kbaseFeedback.unpublishTitle', 'Unpublish article?')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('kbaseFeedback.unpublishConfirm', 'The published document will be deleted and the feedback will return to draft.')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmingUnpublish(false)} disabled={saving} color="inherit">
                        {t('kbaseFeedback.cancel', 'Cancel')}
                    </Button>
                    <Button
                        onClick={unpublishDraft}
                        disabled={saving || selected?.feedbackStatus === 'publishing'}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        {saving ? t('kbaseFeedback.unpublishing', 'Unpublishing…') : t('kbaseFeedback.unpublish', 'Unpublish')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default KbaseFeedback;
